import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent, Tooltip } from "@mui/material";
import {
  getBasicFilteringOptions,
  getClearEventTypeModel,
  getEventTypeFilter,
} from "../../common/EntitiesManagement";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import EventTypeInfo from "./components/EventTypeInfo";
import ModalBackground from "../../components/common/ModalBackground";
import PagePagination from "../../components/PagePagination";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import useGlobalContext from "../../hooks/useGlobalContext";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import { BackForeColor } from "../../components/common/BackForeColor";
import TrueFalseImage from "../../components/TrueFalseImage";
import PaymentTypeValue from "../additionalCostTypes/components/PaymentTypeValue";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function EventType() {
  const {
    eventTypeModel,
    setEventTypeModel,
    eventTypeData,
    EVENTTYPE_API,
    config,
    setPageIndexEventType,
    pageIndexEventType,
    toggleVisibleModalConfirm,
    createEntity,
    updateEntity,
    deleteEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();

  const { id } = useParams();
  const [eventTypeDataFiltered, setEventTypeDataFiltered] = useState();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getEventTypeFilter());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const eventTypeDataObj = eventTypeData.Data.find(
        (eventTypeObj) => eventTypeObj.Id === Number(id)
      );
      setEventTypeModel(
        {
          ...eventTypeDataObj,
          WorkFlowId: eventTypeDataObj.WorkFlow.Id,
          OperationalAreaId: eventTypeDataObj.OperationalArea.Id,
        } || getClearEventTypeModel()
      );
    }
  }, [id]);

  useEffect(() => {
    setEventTypeDataFiltered(eventTypeData.Data);
  }, [eventTypeData.Data]);

  const validateOperationResult = (result, isDelete) => {
    setEventTypeModel(getClearEventTypeModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([EVENTTYPE_API, config]);
    if (isDelete) {
      if (pageIndexEventType !== 0 && eventTypeData.Data.length === 1) {
        setPageIndexEventType(pageIndexEventType - 1);
      }
    }
    window.location.reload(false);
  };

  const handleCreateRequest = async (data) => {
    const createResult = await createEntity(
      `${`${EVENTTYPE_API}`}`,
      data,
      config
    );
    validateOperationResult(createResult, false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${EVENTTYPE_API}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${EVENTTYPE_API}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !eventTypeModel?.Name ||
    !eventTypeModel.operationalArea ||
    !eventTypeModel.Workflow;

  const getColumnTitles = () => {
    return [
      localize.event_type,
      localize.operational_area,
      localize.service_value,
      localize.workflow,
      localize.visit_report,
      localize.request_signature,
      localize.notification,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.event_type_title}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={EVENTTYPE_API}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="event_type"
        />
      )}

      {showEditModal && (
        <ModalBackground
          hasRequiredValues
          isDrawerMode
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_event_type}
          onClickToSubmit={() => {
            handleUpdateRequest(eventTypeModel);
            setShowEditModal(false);
            setEventTypeModel(getClearEventTypeModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setEventTypeModel(getClearEventTypeModel());
          }}
          Component={<EventTypeInfo />}
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.event_type_title}
            subtitle={localize.event_type_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="event_type_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                hasRequiredValues
                isDrawerMode
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_event_type}
                onClickToSubmit={() => {
                  handleCreateRequest(eventTypeModel);
                  setShowAddModal(false);
                  setEventTypeModel(getClearEventTypeModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setEventTypeModel(getClearEventTypeModel());
                }}
                Component={<EventTypeInfo />}
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      <DeleteConfirmationMessage
        entityName={eventTypeModel.Name}
        entityType={localize.event_type}
        onClickForCancel={() => {
          toggleVisibleModalConfirm();
          setEventTypeModel(getClearEventTypeModel());
        }}
        onClickForConfirm={() => {
          handleDeleteRequest(eventTypeModel.Id);
        }}
      />

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexEventType}
            setPageIndex={setPageIndexEventType}
            dataArray={eventTypeData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          eventTypeData.Data?.length !== 0
            ? eventTypeDataFiltered?.map((eventTypeObj) => ({
                key: eventTypeObj.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setEventTypeModel({
                          ...eventTypeObj,
                          WorkFlowId: eventTypeObj.WorkFlow?.Id,
                          Workflow: eventTypeObj.WorkFlow,
                          OperationalAreaId: eventTypeObj.operationalArea?.Id,
                          ReviewFormId: eventTypeObj.ReviewForm?.Id,
                          AdditionalFieldsJson: eventTypeObj.AdditionalFields,
                        });
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setEventTypeModel({
                          ...eventTypeObj,
                        });
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  <BackForeColor
                    backColor={eventTypeObj?.BackgroundColor}
                    foreColor={eventTypeObj?.ForegroundColor}
                    text={eventTypeObj.Name}
                  />,
                  eventTypeObj?.operationalArea ? (
                    <BackForeColor
                      backColor={eventTypeObj?.operationalArea?.BackgroundColor}
                      foreColor={eventTypeObj?.operationalArea?.ForegroundColor}
                      text={eventTypeObj.operationalArea?.Name}
                    />
                  ) : (
                    <ValueOrNoDataLabel data={undefined} />
                  ),
                  <PaymentTypeValue
                    generatesPaymentToTechnician
                    paymentMode="FIXED"
                    paymentValue={eventTypeObj?.executionValue}
                  />,
                  <ValueOrNoDataLabel data={eventTypeObj.WorkFlow?.Name} />,
                  <ValueOrNoDataLabel data={eventTypeObj.ReviewForm?.Name} />,
                  <TrueFalseImage value={eventTypeObj.RequestSignature} />,
                  <TrueFalseImage value={eventTypeObj.createNotification} />,
                  new Date(eventTypeObj.ModificationDate).toLocaleString(
                    undefined,
                    {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    }
                  ),
                  eventTypeObj.CreatedBy,
                ],
              }))
            : [generateNoDataValues(10)]
        }
      />
    </div>
  );
}
